import axios from 'axios';

/** Dedupe identical activity within this window (Strict Mode / remounts). */
const DEDUPE_MS = 2500;

let lastKey = '';
let lastAt = 0;

/**
 * Fire-and-forget UI activity audit event.
 * Backend sets changed_by from the JWT — do not trust client username.
 */
export function logActivity({
  eventType,
  employeeId = null,
  path = '',
  label = '',
  tab = null,
  details = null,
} = {}) {
  if (!eventType) return;

  const reportId =
    details && typeof details === 'object' && typeof details.reportId === 'string'
      ? details.reportId
      : '';
  const key = `${eventType}|${employeeId || ''}|${path}|${tab || ''}|${label || ''}|${reportId}`;
  const now = Date.now();
  if (key === lastKey && now - lastAt < DEDUPE_MS) return;
  lastKey = key;
  lastAt = now;

  axios
    .post('/api/audit-log/activity', {
      eventType,
      employeeId,
      path,
      label,
      tab,
      details,
    })
    .catch(() => {
      // Never block navigation on activity logging failures
    });
}

/**
 * Map the current location to an activity payload, or null if not tracked.
 */
export function activityFromLocation(pathname, search = '') {
  if (!pathname) return null;

  // Auth / public pages — never log
  if (
    pathname.startsWith('/login') ||
    pathname.startsWith('/forgot-password') ||
    pathname.startsWith('/reset-password')
  ) {
    return null;
  }

  const params = new URLSearchParams(search || '');
  const employeeMatch = pathname.match(/^\/employee\/(\d+)\/?$/);

  if (employeeMatch) {
    const employeeId = parseInt(employeeMatch[1], 10);
    let tab = params.get('tab') || 'overview';
    if (tab === 'roles') tab = 'overview';
    if (!['overview', 'notes', 'disciplinary', 'documents', 'employee-file', 'events', 'wage-history', 'schedule-prefs'].includes(tab)) {
      tab = 'overview';
    }

    if (tab === 'overview' && !params.get('tab')) {
      return {
        eventType: 'employee_viewed',
        employeeId,
        path: pathname,
        label: 'Employee',
        tab: 'overview',
      };
    }

    return {
      eventType: 'employee_tab_viewed',
      employeeId,
      path: `${pathname}${search || ''}`,
      label: 'Employee',
      tab,
    };
  }

  const pages = [
    { test: (p) => p.startsWith('/settings'), label: 'Settings', path: '/settings' },
    { test: (p) => p === '/' || p === '/employees', label: 'Employees', path: '/' },
    { test: (p) => p.startsWith('/users'), label: 'Users', path: '/users' },
    { test: (p) => p.startsWith('/audit-log'), label: 'Audit Log', path: '/audit-log' },
    { test: (p) => p.startsWith('/time-cards'), label: 'Timecards', path: '/time-cards' },
    { test: (p) => p.startsWith('/time-off'), label: 'Time Off', path: '/time-off' },
    { test: (p) => p.startsWith('/roles'), label: 'Roles', path: '/roles' },
    { test: (p) => p.startsWith('/items'), label: 'Items', path: '/items' },
    { test: (p) => p.startsWith('/modifiers'), label: 'Modifiers', path: '/modifiers' },
    { test: (p) => p.startsWith('/inventory'), label: 'Inventory', path: '/inventory' },
    { test: (p) => p.startsWith('/inventory'), label: 'Inventory', path: '/inventory' },
    { test: (p) => p.startsWith('/invoices'), label: 'Invoices', path: '/invoices' },
    { test: (p) => p.startsWith('/gift-cards'), label: 'Gift Cards', path: '/gift-cards' },
    { test: (p) => p.startsWith('/transactions'), label: 'Transactions', path: '/transactions' },
    { test: (p) => p.startsWith('/scheduling'), label: 'Scheduling', path: '/scheduling' },
    { test: (p) => p.startsWith('/schedule-prefs'), label: 'Schedule Prefs', path: '/schedule-prefs' },
    { test: (p) => p.startsWith('/printing'), label: 'Printing', path: '/printing' },
    { test: (p) => p.startsWith('/documents'), label: 'Documents', path: '/documents' },
    { test: (p) => p.startsWith('/payroll'), label: 'Payroll', path: '/payroll' },
    { test: (p) => p.startsWith('/cash-tips'), label: 'Cash Tips', path: '/cash-tips' },
    { test: (p) => p.startsWith('/tip-pool'), label: 'Tip Pool', path: '/tip-pool' },
    { test: (p) => p.startsWith('/profile'), label: 'Profile', path: '/profile' },
    { test: (p) => p.startsWith('/square-import'), label: 'Import', path: '/square-import' },
  ];

  // Reports page logs specific report views from Reporting.js (report_viewed).
  if (pathname.startsWith('/reports') || pathname.startsWith('/reporting')) {
    return null;
  }

  for (const page of pages) {
    if (page.test(pathname)) {
      return {
        eventType: 'page_viewed',
        employeeId: null,
        path: page.path,
        label: page.label,
        tab: null,
      };
    }
  }

  return null;
}
